import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { SubmissionError } from 'redux-form';
import { reduxFormErrorMapper } from 'erpcore/components/Form/Form.utils';
import { withRouter } from 'react-router-dom';
import { dtoForm } from 'erpcore/utils/dto';
import { diff } from 'deep-object-diff';
import LayoutManager from 'erpcore/utils/LayoutManager';
import PageContent from 'erpcore/components/Layout/PageContent';
import UserEditForm from 'erpcore/screens/Users/components/UserEditForm';
import PageLoader from 'erpcore/components/PageLoader';
import { actions as usersActions } from 'erpcore/screens/Users/Users.reducer';
import { getUserData, getSingleUserFetching } from 'erpcore/screens/Users/Users.selectors';
import UserEditTabs from 'erpcore/screens/Users/components/UserEditTabs';
import UserEditPageHeader from 'erpcore/screens/Users/components/UserEditPageHeader';

import { setLocationData } from 'erpcore/components/Form/components/Location/dto';
import { dtoIgnoreTimezone, insertIf } from 'erpcore/utils/utils';

const UserEdit = ({ match }) => {
    const dispatch = useDispatch();
    const userIri = `/api/users/${match?.params?.id}`;
    const userData = useSelector(state => dtoForm(getUserData(state, userIri))) || {};
    const fetching = useSelector(state => getSingleUserFetching(state));
    const initialValues = {
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        personal_email: userData.personal_email,
        phone_number: userData.phone_number || '+1',
        is_active: userData.is_active,
        birthdate: dtoIgnoreTimezone(userData.birthdate),
        isBirthdatePrivate: userData.is_birthdate_private,
        location: {
            full_address: userData.full_address,
            street: userData.street,
            country: userData.country,
            state: userData.state,
            city: userData.city,
            zip: userData.zip,
            latitude: userData.latitude,
            longitude: userData.longitude
        },
        t_shirt_size: userData.t_shirt_size,
        image: userData.image,
        gender: userData.gender,
        _type: userData._type,
        lastLoggedIn: userData.last_activity,
        timezone: userData?.timezone,
        userTypes: userData?.userTypes?.[0]
    };

    const pageTitle = () => {
        const { first_name: firstName, last_name: lastName } = { ...userData };
        if (firstName || lastName) {
            return `Edit user - ${firstName} ${lastName}`;
        }

        return `Edit user`;
    };

    const fetchUserData = () => {
        const params = {
            include: 'organizations,country,state,city,image,timezone,roles,permissions'
        };
        return new Promise((resolve, reject) => {
            dispatch({
                promise: { resolve, reject },
                type: usersActions.START_FETCHING_SINGLE_USER,
                iri: userIri,
                params
            });
        }).catch(error => ({ error }));
    };

    const onSubmit = formData => {
        // send only changed data
        formData = diff(initialValues, formData);

        // Handle Location data
        const { location } = { ...formData };
        if (location) {
            formData = setLocationData('location', formData);
        }

        // Handle User Type; send userTypes, roles and permissions together in "roles" parameter
        if ('userTypes' in formData) {
            formData.roles = [
                ...insertIf(formData?.userTypes, [formData.userTypes]),
                ...insertIf(userData?.roles?.length, userData.roles),
                ...insertIf(userData?.permissions?.length, userData.permissions)
            ];
            delete formData.userTypes;
        }

        return new Promise((resolve, reject) =>
            dispatch({
                promise: { resolve, reject },
                type: usersActions.START_UPDATE_SINGLE_USER,
                iri: userIri,
                formData
            })
        ).catch(error => {
            throw new SubmissionError(reduxFormErrorMapper(error));
        });
    };

    /*
     * Effects
     */
    useEffect(() => {
        fetchUserData();
    }, []);

    return (
        <LayoutManager slot="main" className="main--narrow" layoutType="merge">
            <UserEditPageHeader pageTitle={pageTitle()} />
            <UserEditTabs />
            <PageContent>
                {fetching === true && <PageLoader content />}
                <UserEditForm
                    onSubmit={onSubmit}
                    form="UserEditForm"
                    initialValues={initialValues}
                    submitLabel="Update"
                    userId={match?.params?.id}
                />
            </PageContent>
        </LayoutManager>
    );
};

UserEdit.defaultProps = {
    match: {}
};

UserEdit.propTypes = {
    match: PropTypes.oneOfType([PropTypes.object])
};

export default withRouter(UserEdit);
